import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { App } from "antd";
import {
  LESSONS,
  LOADING_LESSONS,
  LESSONS_PAGINATION,
} from "../store/selectors";
import {
  setLoading,
  setLessonsWithPagination,
  deleteLesson as deleteLessonAction,
} from "../store/slices/lesson";
import {
  getLessons,
  getAllLessons,
  deleteLesson as deleteLessonService,
  getLessonById as getLessonByIdService,
} from "../services/lessons/lessons.service";
import { SearchLessonParams } from "../services/lessons/lessons.types";
import { LessonMinimalResponse } from "../types/api/getLesson";

export const useLessons = () => {
  // From App.useApp(): the static notification does not render under React 19.
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const lessons = useSelector(LESSONS);
  const loading = useSelector(LOADING_LESSONS);
  const pagination = useSelector(LESSONS_PAGINATION);

  const [searchParams, setSearchParams] = useState<SearchLessonParams>({
    page: 1,
    pageSize: 10,
  });

  const fetchLessons = useCallback(
    async (params?: SearchLessonParams) => {
      dispatch(setLoading(true));
      try {
        const finalParams = { ...searchParams, ...params };
        const lessonsResponse = await getLessons(finalParams);
        dispatch(setLessonsWithPagination(lessonsResponse));
      } catch (error: any) {
        dispatch(setLoading(false));
        notification["error"]({
          message: "Error",
          description:
            "La información no pudo ser cargada, por favor notifica éste error!",
          placement: "top",
          duration: 3,
        });
        console.error("Error fetching lessons:", error);
        // No re-lanzar el error para evitar promise rejections no manejadas
      }
    },
    [dispatch, notification, searchParams]
  );

  const updateSearchParams = useCallback(
    async (newParams: Partial<SearchLessonParams>) => {
      const updatedParams = { ...searchParams, ...newParams };
      if (
        newParams.lessonTitle !== undefined ||
        newParams.courseTitle !== undefined ||
        newParams.lessonId !== undefined
      ) {
        updatedParams.page = 1;
      }
      setSearchParams(updatedParams);
      // Manejar el error aquí en lugar de dejarlo propagarse
      try {
        await fetchLessons(updatedParams);
      } catch (error) {
        console.error("Error updating search params:", error);
      }
    },
    [searchParams, fetchLessons]
  );

  const changePage = useCallback(
    async (page: number, pageSize?: number) => {
      const newParams: Partial<SearchLessonParams> = { page };
      if (pageSize) {
        newParams.pageSize = pageSize;
      }
      // Manejar el error aquí
      try {
        await updateSearchParams(newParams);
      } catch (error) {
        console.error("Error changing page:", error);
      }
    },
    [updateSearchParams]
  );

  const getLessonById = async (id: number) => {
    try {
      const response = await getLessonByIdService(id);
      return response;
    } catch (error: any) {
      // Caller owns the error UX; rethrow the original error so it can read
      // error.response.data.message.
      console.error("Error getting lesson by ID:", error);
      throw error;
    }
  };

  const deleteLesson = async (id: number): Promise<boolean> => {
    dispatch(setLoading(true));
    try {
      await deleteLessonService(id);
      dispatch(deleteLessonAction(id));
      notification["success"]({
        message: "Éxito",
        description: "La lección fue eliminada exitosamente!",
        placement: "top",
        duration: 3,
      });
      dispatch(setLoading(false));
      // Manejar el error de fetchLessons aquí
      try {
        await fetchLessons();
      } catch (error) {
        console.error("Error refetching lessons after delete:", error);
        // La notificación de error ya se muestra en fetchLessons
      }
      return true;
    } catch (error: any) {
      dispatch(setLoading(false));
      console.error("Error deleting lesson:", error);
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.response?.data?.message ||
          "La lección no pudo ser eliminada!",
        placement: "top",
        duration: 3,
      });
      return false;
    }
  };

  const getAllLessonsData = async (): Promise<LessonMinimalResponse[]> => {
    try {
      const response = await getAllLessons();
      return response || [];
    } catch (error: any) {
      notification["error"]({
        message: "Error",
        description:
          "No se pudieron cargar las lecciones, por favor notifica este error!",
        placement: "top",
        duration: 3,
      });
      console.error("Error getting all lessons:", error);
      return [];
    }
  };

  useEffect(() => {
    // Manejar el error de la carga inicial
    fetchLessons().catch((error) => {
      console.error("Error in initial fetch:", error);
      // La notificación ya se muestra en fetchLessons
    });
  }, []);

  return {
    loading,
    lessons,
    pagination,
    searchParams,
    deleteLesson,
    getLessonById,
    fetchLessons,
    updateSearchParams,
    changePage,
    getAllLessonsData,
  };
};
